import { useState } from "react";

import { useCreateStudy } from "../../hooks/useProjects";

interface Props {
  projectId: string;
  onClose: () => void;
}

const NAME_MAX = 255;

export function AddStudyModal({ projectId, onClose }: Props) {
  const [name, setName] = useState("");
  // Stage 10 default: ``ac_block_layout`` (AC-block flooding packer).
  // ``array_layout`` (the Stage 9 single-tracker grid) is still
  // reachable via the API; expose a workflow-type <select> here once
  // there's a real reason to pick the legacy path manually.
  const [validationError, setValidationError] = useState<string | null>(null);
  const mutation = useCreateStudy(projectId);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    const trimmed = name.trim();
    if (trimmed.length < 1) {
      setValidationError("Study name is required.");
      return;
    }
    if (trimmed.length > NAME_MAX) {
      setValidationError(`Study name must be ≤ ${NAME_MAX} characters.`);
      return;
    }

    try {
      await mutation.mutateAsync({ name: trimmed });
      onClose();
    } catch {
      // mutation.isError surfaces the failure inline below.
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-study-title"
      data-testid="add-study-modal"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
      }}
    >
      <div
        style={{
          background: "white",
          padding: "1.5rem",
          borderRadius: 12,
          width: "min(28rem, 90vw)",
          border: "1px solid #E3E8EE",
        }}
      >
        <h2 id="add-study-title" style={{ margin: "0 0 1rem", fontSize: "1.05rem", fontWeight: 600 }}>
          New Study
        </h2>
        <form onSubmit={onSubmit}>
          <label
            htmlFor="study-name"
            style={{ display: "block", marginBottom: "0.25rem" }}
          >
            Name
          </label>
          <input
            id="study-name"
            data-testid="study-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={NAME_MAX + 1}
            required
            autoFocus
            style={{
              width: "100%",
              marginBottom: "0.75rem",
              padding: "0.5rem",
              border: "1px solid #E3E8EE",
              borderRadius: 6,
            }}
          />

          <div style={{ marginBottom: "1rem", color: "#666", fontSize: "0.875rem" }}>
            Workflow type: <code>ac_block_layout</code> (AC-block flooding —
            78 × 108_INT + 2 × 81_EPN + 1 inverter per block)
          </div>

          {validationError && (
            <div role="alert" style={{ color: "#dc2626", marginBottom: "0.5rem" }}>
              {validationError}
            </div>
          )}
          {mutation.isError && (
            <div role="alert" style={{ color: "#dc2626", marginBottom: "0.5rem" }}>
              {mutation.error instanceof Error
                ? mutation.error.message
                : "Failed to create study."}
            </div>
          )}

          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={mutation.isPending}
              style={{
                padding: "0.55rem 1rem",
                borderRadius: 6,
                border: "1px solid #E3E8EE",
                background: "white",
                cursor: mutation.isPending ? "default" : "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="add-study-submit"
              disabled={mutation.isPending}
              style={{
                padding: "0.55rem 1rem",
                borderRadius: 6,
                border: "none",
                background: "#635bff",
                color: "white",
                fontWeight: 500,
                cursor: mutation.isPending ? "default" : "pointer",
                opacity: mutation.isPending ? 0.7 : 1,
              }}
            >
              {mutation.isPending ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
